import { isEmpty, includes, toLower, compact, reduce, camelCase, sortBy } from "lodash"

const NAME_PATTERN = /^[A-Za-z0-9_\-./,\\\s]{3,50}$/

export const validateCustomViewName = name => {
  return name.match(NAME_PATTERN)
}

function matchesSearchTerm(text, searchTerm) {
  return includes(toLower(text), toLower(searchTerm))
}

function filterMetricItem(metric, searchValue) {
  if (metric.metricTypeOptions) {
    const matchesSearch = matchesSearchTerm(metric.label, searchValue)
    if (!matchesSearch) return null

    return {
      ...metric,
      itemsFound: metric.items.length,
    }
  }

  if (!metric.items) {
    const matchesSearch = matchesSearchTerm(metric.label, searchValue)
    return matchesSearch ? metric : null
  }

  const filteredItems = metric.items
    .map(item => filterMetricItem(item, searchValue))
    .filter(Boolean)

  if (filteredItems.length === 0) return null

  const stats = createStats(filteredItems)

  return {
    ...metric,
    items: filteredItems,
    ...stats,
  }
}

function createStats(items) {
  return items.reduce((acc, item) => ({
    itemsFound: acc.itemsFound + (item.itemsFound || 0),
    itemsSelected: acc.itemsSelected + (item.itemsSelected || 0),
    totalItems: acc.totalItems + (item.totalItems || 0),
  }), { itemsFound: 0, itemsSelected: 0, totalItems: 0 })
}

export function processMetrics(availableMetrics, selectedMetricsColumns) {
  if (!availableMetrics) {
    return {}
  }

  // Convert selected metrics array to object for faster lookups
  const selectedMetrics = compact(selectedMetricsColumns).reduce((acc, value) => {
    acc[value] = true
    return acc
  }, {})

  // Process a single metric item and its children
  function processMetricItem(metric, parentSelector = "") {
    const currentSelector = compact([parentSelector, metric.id]).join(".")
    const isSelected = !!selectedMetrics[currentSelector]
      || (metric.metricTypeOptions?.length === 1 && !!selectedMetrics[`${currentSelector}.${metric.metricTypeOptions[0].id}`])

    // Handle items with metric type options
    if (metric.metricTypeOptions) {
      const processedOptions = metric.metricTypeOptions.map(option => ({
        ...option,
        selector: `${currentSelector}.${option.id}`,
        selected: !!selectedMetrics[`${currentSelector}.${option.id}`],
      }))

      return {
        ...metric,
        selector: currentSelector,
        selected: isSelected,
        itemsFound: processedOptions.length,
        itemsSelected: processedOptions.filter(opt => opt.selected).length,
        totalItems: processedOptions.length,
        shouldShowItems: true,
        items: processedOptions,
      }
    }

    // Handle leaf nodes (metrics without children)
    if (!metric.items) {
      return {
        ...metric,
        selector: currentSelector,
        selected: isSelected,
        itemsFound: 1,
        itemsSelected: isSelected ? 1 : 0,
        totalItems: 1,
        shouldShowItems: true,
      }
    }

    // Process child items
    const sortedItems = sortBy(metric.items, [
      item => !isEmpty(item.items),
      item => toLower(item.label),
    ])

    const processedItems = sortedItems
      .map(item => processMetricItem(item, currentSelector))
      .filter(Boolean)

    const stats = createStats(processedItems)

    return {
      ...metric,
      selector: currentSelector,
      selected: isSelected,
      items: processedItems,
      shouldShowItems: true,
      ...stats,
    }
  }

  // Process all available metrics
  return reduce(availableMetrics, (result, metric) => {
    const processedMetric = processMetricItem(metric)
    if (!processedMetric) return result

    if (metric.contentSetId) {
      return {
        ...result,
        contentSets: [...(result.contentSets || []), processedMetric],
      }
    }

    return {
      ...result,
      [camelCase(metric.id)]: processedMetric,
    }
  }, {})
}

export function filterProcessedMetrics(processedMetrics, searchValue) {
  if (!searchValue || isEmpty(processedMetrics)) return processedMetrics

  const result = {}

  if (processedMetrics.contentSets) {
    const filteredContentSets = processedMetrics.contentSets
      .map(contentSet => filterMetricItem(contentSet, searchValue))
      .filter(Boolean)

    if (filteredContentSets.length > 0) {
      result.contentSets = filteredContentSets
    }
  }

  Object.keys(processedMetrics).forEach(key => {
    if (key === "contentSets") return

    const filteredMetric = filterMetricItem(processedMetrics[key], searchValue)
    if (filteredMetric) {
      result[key] = filteredMetric
    }
  })

  return result
}
